import type { EmailValidationVerdict } from "@workspace/api-client-react";
import { AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";

const REASON_LABELS: Record<string, string> = {
  bad_format: "Address is not a valid email format",
  no_mx: "Domain has no MX records",
  undeliverable: "Provider says this address won't accept mail",
  disposable: "Disposable / throwaway email provider",
  role: "Role address (e.g. info@, sales@)",
  risky: "Provider flagged this address as risky",
  unknown: "Provider could not determine deliverability",
  smtp_uncertain: "SMTP check did not confirm the mailbox",
  validator_unavailable: "Email validation service was unavailable",
  empty: "Address is empty",
};

export function reasonLabel(code: string): string {
  return REASON_LABELS[code] ?? code;
}

type Status = "valid" | "risky" | "invalid";

type VerdictLike = {
  status: Status;
  reasons?: string[] | null;
};

const STYLES: Record<Status, string> = {
  valid:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
  risky:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
  invalid:
    "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
};

const LABELS: Record<Status, string> = {
  valid: "Verified",
  risky: "Risky",
  invalid: "Invalid",
};

const Icon = {
  valid: CheckCircle2,
  risky: AlertTriangle,
  invalid: ShieldAlert,
} as const;

/**
 * Compact pill that summarises an Abstract API email-validation
 * verdict. Used on the Contacts list and inside the Compose error
 * banner. The tooltip / `title` attribute carries the full list of
 * verdict reasons so users can hover to see *why* an address was
 * flagged without having to re-validate.
 */
export function EmailVerdictBadge({
  verdict,
  className,
}: {
  verdict: VerdictLike | EmailValidationVerdict;
  className?: string;
}) {
  const status = verdict.status as Status;
  const reasons = verdict.reasons ?? [];
  const IconCmp = Icon[status];
  const tooltip = reasons.length
    ? reasons.map(reasonLabel).join(" • ")
    : LABELS[status];
  return (
    <span
      data-testid={`badge-verdict-${status}`}
      title={tooltip}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        STYLES[status],
        className,
      )}
    >
      <IconCmp className="h-3 w-3" />
      {LABELS[status]}
    </span>
  );
}
